import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../api';
import './AndroidPackagePanel.css';
import './TargetPicker.css';

const POLL_MS = 10000;
const STATE_TEXT = {
  QUEUED: 'בתור לבנייה',
  RUNNING: 'נבנה ונבדק…',
  READY: 'מוכן – הותקן והופעל בהצלחה',
  FAILED: 'נכשל',
  UNAVAILABLE: 'לא זמין כרגע (הוחזר)'
};
const FORMAT_TEXT = { exe: 'קובץ התקנה (.exe)', AppImage: 'AppImage', deb: 'חבילת .deb', dmg: 'קובץ .dmg', zip: 'ZIP' };

export function desktopStateText(state) { return STATE_TEXT[state] || ''; }

/**
 * Universal Desktop packaging for a verified project: one price for Windows + Linux + macOS,
 * shown by the server and approved explicitly before anything is charged. Each system then has
 * its own status, download and free retry. The verified Preview is never changed.
 */
export default function DesktopPackagePanel({ projectId }) {
  const [pkg, setPkg] = useState(null);
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const approvalKey = useRef(null);
  const timer = useRef(null);

  const load = useCallback(async () => {
    if (!projectId) return;
    try { setPkg((await api.desktopPackageStatus(projectId)).desktopPackage || null); }
    catch (err) { setError(err.response?.data?.error || ''); }
  }, [projectId]);

  useEffect(() => { load(); return () => clearTimeout(timer.current); }, [load]);
  useEffect(() => {
    clearTimeout(timer.current);
    if (pkg?.state === 'RUNNING') timer.current = setTimeout(load, POLL_MS);
    return () => clearTimeout(timer.current);
  }, [pkg, load]);

  const askPrice = async () => {
    setBusy(true); setError('');
    try {
      const data = await api.desktopPackageQuote(projectId);
      setQuote(data.quote);
      approvalKey.current = `${data.quote.quoteId}`;
    } catch (err) { setError(err.response?.data?.error || 'לא הצלחנו לקבל מחיר כרגע.'); }
    finally { setBusy(false); }
  };

  const approve = async () => {
    if (!quote) return;
    setBusy(true); setError('');
    try {
      const data = await api.requestDesktopPackage(projectId, quote.quoteId, approvalKey.current);
      setPkg(data.desktopPackage); setQuote(null);
    } catch (err) {
      const body = err.response?.data || {};
      setError(body.error || 'האריזה לא התחילה.');
      if (body.requote || body.code === 'QUOTE_EXPIRED') setQuote(null);
    } finally { setBusy(false); }
  };

  const retry = async platform => {
    setBusy(true); setError('');
    try { setPkg((await api.retryDesktopPlatform(projectId, platform)).desktopPackage); }
    catch (err) { setError(err.response?.data?.error || 'הניסיון החוזר לא התחיל.'); }
    finally { setBusy(false); }
  };

  const download = async (platform, file) => {
    setError('');
    try {
      const { download: link } = await api.desktopPackageDownload(projectId, platform, file);
      window.location.assign(link.url);
    } catch (err) { setError(err.response?.data?.error || 'ההורדה לא זמינה כרגע.'); }
  };

  if (!projectId) return null;
  const platforms = pkg?.platforms ? Object.values(pkg.platforms) : [];

  return (
    <div className="android-package-panel desktop-package-panel" dir="rtl" data-testid="desktop-package">
      <strong>תוכנה למחשב: Windows, Linux ו־macOS</strong>
      <p className="android-package-hint">חבילה אחת כוללת את שלוש מערכות ההפעלה. כל מערכת נבנית, מותקנת ומופעלת בבדיקה על מחשב של אותה מערכת לפני שהיא מוצגת כמוכנה.</p>

      {pkg?.needsPreview && <p className="android-package-hint">קודם צריך Preview מאומת של התוכנה; האריזה נבנית ממנו.</p>}

      {platforms.length > 0 && (
        <ul className="desktop-package-platforms">
          {platforms.map(entry => (
            <li key={entry.platform} data-state={entry.state}>
              <span className="desktop-platform-name">{entry.label}</span>
              <span className="desktop-platform-state">{desktopStateText(entry.state)}{entry.state === 'FAILED' && entry.attempts ? ` (ניסיון ${entry.attempts} מתוך ${entry.maxAttempts})` : ''}</span>
              {entry.state === 'READY' && entry.files.map(file => (
                <button key={file.name} type="button" className="btn" onClick={() => download(entry.platform, file.name)}>הורדה: {FORMAT_TEXT[file.format] || file.name}</button>
              ))}
              {entry.state === 'READY' && entry.platform === 'MACOS' && entry.signing !== 'developer-id' && <small>בפתיחה הראשונה ב־Mac: לחיצה ימנית על התוכנה ← "פתח" (התוכנה עדיין לא חתומה בחשבון מפתח של Apple).</small>}
              {entry.state === 'READY' && entry.platform === 'WINDOWS' && entry.signing !== 'authenticode' && <small>Windows עשוי להציג אזהרת SmartScreen ("מידע נוסף" ← "הפעל בכל זאת") עד שתתווסף חתימה דיגיטלית.</small>}
              {entry.canRetry && <button type="button" className="btn" disabled={busy} onClick={() => retry(entry.platform)}>ניסיון חוזר ללא חיוב</button>}
              {entry.shareRefunded && entry.shareCredits > 0 && <small>{entry.shareCredits} קרדיטים הוחזרו עבור מערכת זו.</small>}
            </li>
          ))}
        </ul>
      )}

      {!quote && pkg && pkg.state !== 'RUNNING' && !pkg.needsPreview && (pkg.state === 'NONE' || pkg.needsNewVersion || ['READY', 'PARTIAL', 'FAILED'].includes(pkg.state)) && (
        <button type="button" className="submit-btn" disabled={busy || pkg.available === false} onClick={askPrice}>
          {pkg.state === 'NONE' ? 'בדיקת מחיר לאריזת התוכנה' : 'אריזה מחדש (גרסה חדשה)'}
        </button>
      )}

      {quote && (
        <div className="desktop-package-quote" role="group" aria-label="אישור מחיר">
          <p><strong>המחיר: {quote.credits} קרדיטים</strong> עבור Windows + Linux + macOS יחד (חיוב אחד).</p>
          <ul>{quote.platforms.map(item => <li key={item.platform}>{item.label}: {item.willBuild ? 'ייבנה' : 'לא זמין כרגע – לא תחויב עליו'}</li>)}</ul>
          <p className="android-package-hint">{quote.refundPolicy}</p>
          <p>היתרה שלך: {quote.balance} קרדיטים.</p>
          {quote.enoughCredits ? (
            <div className="desktop-package-quote-actions">
              <button type="button" className="submit-btn" disabled={busy} onClick={approve}>אישור וחיוב {quote.credits} קרדיטים</button>
              <button type="button" className="btn" disabled={busy} onClick={() => setQuote(null)}>ביטול</button>
            </div>
          ) : (
            <p role="alert">אין מספיק קרדיטים. הפרויקט שמור; אפשר להוסיף קרדיטים במסך החשבון ולחזור לכאן.</p>
          )}
        </div>
      )}

      {error && <p className="android-package-error" role="alert">{error}</p>}
    </div>
  );
}
